import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, Observable, of, shareReplay, tap } from "rxjs";
import { UiServicesUtils } from "./ui-services.utils.service";
import { UiServiceWebSession } from "./ui-services.web-session.service";
import { UiServicesLogger } from "./ui-services.logger.service";
import { DRCTSchedule, DRCTScheduleList, DRCTScheduleView } from "@drct-web-apps/ui-types";



@Injectable({
  providedIn: 'root'
})
export class UiServicesSchedule {
    private readonly fullyReadySubject = new BehaviorSubject<boolean>(false);
    fullyReady$ = this.fullyReadySubject.asObservable();

    private _loading       = false;
    public winter_schedule = false;

    private readonly http       = inject(HttpClient);
    private readonly utils      = inject(UiServicesUtils);
    private readonly webSession = inject(UiServiceWebSession)
    private readonly logger     = inject(UiServicesLogger);

    constructor() {
        // Auto-trigger schedule load once webSession.sessionReady = true
        this.webSession.sessionReady
            .pipe(filter(ready => ready))
            .subscribe(() => {
                this.winter_schedule = this.webSession.webConfig?.winter_schedule ?? false;
                this.loadScheduleOnce();
            });
    }

    public waitUntilFullyReady(): Observable<boolean> {
        return this.fullyReady$.pipe(filter(r => r === true));
    }

    public getScheduleInfo(schedule: DRCTScheduleList, type: string, isHome = false): string {
        let schedInfo  = '';
        let winterInfo = '';
        const schedObj = this.convertScheduleInfo(schedule);

        switch(type) {
            case 'TITLE':
                schedInfo = schedObj.sched_description;
                if(isHome) {
                    schedInfo = schedObj.sched_short_description? schedObj.sched_short_description: schedInfo;
                }
                break;
            case 'DAY':
                schedInfo = schedObj.sched_day;
                if(isHome) {
                    schedInfo = schedObj.sched_short_day? schedObj.sched_short_day: schedInfo;
                }
                break;
            case 'TIME':
                schedInfo = schedObj.sched_time ?? '';
                winterInfo = this.webSession.webConfig?.winter_schedule? (schedObj.sched_winter_time ?? '') 
                                                                       : schedInfo;
                if(this.webSession.webConfig?.winter_schedule && winterInfo && winterInfo !== schedInfo) {
                    schedInfo = winterInfo;
                }
                break;
            case 'NOTE': {
                schedInfo = schedObj.sched_short_notes ?? '';
                winterInfo = this.webSession.webConfig?.winter_schedule? (schedObj.sched_short_winter_notes ?? '')
                                                                       : schedInfo;
                if(this.webSession.webConfig?.winter_schedule && winterInfo && winterInfo !== schedInfo) {
                    schedInfo = winterInfo;
                }
                break;
            }
            default:
                schedInfo = '';
                break;
        }

        return schedInfo;
    }


    private loadScheduleOnce(): void {
        if(this._loading || (this.webSession.scheduleInfo && (this.webSession.scheduleInfo?.length ?? 0) > 0)) {
            this.logger.log("Web Schedule Already Loaded. Skip Loading...");
            return;
        }

        this._loading = true;
        this.logger.log("Loading Web Schedule Info...");

        const formData = new FormData();
        formData.append("frmData", JSON.stringify({ 
            opr: 'schedule-get', 
            uuid: (this.webSession.userName ?? '')
        } ));

        this.http.post<DRCTScheduleView>(this.utils.builApiUrlWithEnv("drct_schedule.php"), formData)
                 .pipe(tap(response => {
                    this.webSession.scheduleInfo = [];
                    this.logger.log("Web Schedule Response. Total: " + response.tot_rows);
                    if(response.success && response.tot_rows &&  response.tot_rows > 0) {
                        this.webSession.scheduleInfo = response.schedule ?? [];
                    }

                    this.fullyReadySubject.next(true);
                  }),
                  catchError(error => {
                    this.logger.error("Web Schedule Load Error: " + error);
                    this.webSession.scheduleInfo = [];
                    this.fullyReadySubject.next(false);
                    return of(null);
                  }),
                  shareReplay(1)
                 )
                .subscribe();
    }

    private convertScheduleInfo(schedule: DRCTScheduleList): DRCTSchedule {
        const scheduleInfo = typeof schedule.schedule_info === 'string'
                                ? JSON.parse(schedule.schedule_info)
                                : (schedule.schedule_info? schedule.schedule_info: {});

        return {
            sched_description: schedule.schedule_description ?? '',
            sched_day: scheduleInfo.sched_day ?? '',
            sched_short_description: scheduleInfo.sched_short_description ?? '',
            sched_short_day: scheduleInfo.sched_short_day ?? '',
            sched_time: scheduleInfo.sched_time ?? '',
            sched_winter_time: scheduleInfo.sched_winter_time ?? '',
            sched_notes: scheduleInfo.sched_notes ?? '',
            sched_short_notes: scheduleInfo.sched_short_notes ?? ''
        };
    }
}